/**
 * Shared accordion content for the ECQ Q-SUITE and SHRED products.
 * Used by both the home-page sections and the product detail pages so the
 * copy stays in a single source of truth.
 */

export interface AccordionEntry {
  /** Card label shown in both collapsed and expanded states. */
  title: string;
  /** Optional bold lead-in shown above the description when expanded. */
  headline?: string;
  /** Body copy revealed when the card is expanded. */
  description: string;
  /** Bullet list shown beneath "Core Capabilities:" on the product detail page. */
  coreCapabilities?: string[];
  /** Optional trailing note rendered below the bullet list. */
  footer?: string;
}

/**
 * Q-SUITE packs — shared by the home teaser (Home/QSuite.tsx) and the
 * /products/q-suite detail page (Figma 731-74594).
 */
export const qSuitePacks: AccordionEntry[] = [
  {
    title: "Q-ENTERPRISE",
    headline: "Validate Enterprise Applications. Verify Security Controls.",
    description:
      "Q-ENTERPRISE provides offensive security content targeting widely deployed enterprise applications, collaboration platforms, productivity software, identity services, network infrastructure, and cloud-connected business systems. The content is continuously updated through ECQ's vulnerability research and AI-assisted analysis pipeline, enabling organizations to validate security controls against the software most commonly used across modern enterprise environments.",
    coreCapabilities: [
      "Enterprise software exploitation",
      "Productivity & collaboration platforms",
      "Email & messaging systems",
      "Infrastructure & middleware technologies",
      "Remote access & management solutions",
    ],
  },
  {
    title: "Q-OT",
    headline: "Test the Technologies That Keep the World Running.",
    description:
      "Q-OT delivers offensive security content specifically designed for Operational Technology and Industrial Control System environments. Covering industrial software, engineering workstations, HMIs, historians, SCADA platforms, industrial protocols, and OT infrastructure, Q-OT enables organizations to safely evaluate their ability to detect, respond to, and recover from threats targeting critical operational processes.",
    coreCapabilities: [
      "Industrial Control Systems (ICS)",
      "SCADA & HMI Platforms",
      "Engineering Workstations",
      "Historians & OPC Technologies",
      "Critical Infrastructure Systems",
    ],
  },
  {
    title: "Q-CONNECTED",
    headline: "From Firmware to Edge. Validate Everything.",
    description:
      "Q-CONNECTED focuses on the rapidly growing ecosystem of connected technologies, including network appliances, embedded systems, smart devices, industrial gateways, edge computing platforms, and IoT infrastructure. The pack enables security teams to assess exposure to device-specific vulnerabilities, firmware weaknesses, management interface attacks, and emerging threats affecting connected environments.",
    coreCapabilities: [
      "IoT Devices & Smart Technologies",
      "Embedded Systems & Firmware",
      "Network Appliances & Gateways",
      "Edge Computing Platforms",
      "Industrial IoT Technologies",
    ],
  },
  {
    title: "Q-RESEARCH",
    headline: "Bridge the Gap Between Offense and Defense.",
    description:
      "Q-RESEARCH provides priority access to emerging threat intelligence, offensive security research, and early validation content derived from vulnerability research, exploit development, and real-world attack investigations. Designed for MDR providers, SOC teams, threat hunters, and mature security programs, Q-RESEARCH enables organizations to strengthen detections and validate security controls before threats become widely adopted in the wild.",
    coreCapabilities: [
      "Early access attack content",
      "Emerging threat intelligence",
      "Detection engineering support",
      "Threat hunting validation",
      "Rapid exploit intelligence",
    ],
  },
];

export const shredItems: AccordionEntry[] = [
  {
    title: "KhaiCode Engine",
    description:
      "Built in collaboration with KhaiCode Binary Exploit Intelligence Platform, this engine automates the discovery and exploitation of known vulnerabilities in compiled binaries with no source code required. It decomposes software components, maps them against CVE records, traces exploitable attack paths, and validates exploitability, giving results from scan to confirmed exploit in hours.",
  },
  {
    title: "AI-Powered Vulnerability Research",
    description:
      "An AI-orchestrated engine that drives the complete vulnerability discovery and exploitation workflow. It maps the attack surface, coordinates fuzzing and analysis in parallel, provisions lab environments automatically, and guides exploit development. Research cycles that used to take weeks now take days.",
  },
  {
    title: "Fuzzing Management Platform",
    description:
      "A managed fuzzing environment that takes the operational burden off research teams. Teams configure and launch campaigns in minutes, scale to hundreds of parallel instances automatically, and receive deduplicated, classified results that are ready for review, not raw output to sort through.",
  },
  {
    title: "Comprehensive Leaked Data Reports",
    description:
      "Detailed intelligence reports that consolidate leaked credentials, source code, and sensitive assets discovered across public and underground channels. Each report is enriched with context, severity, and remediation guidance so teams can prioritize response and contain exposure quickly.",
  },
  {
    title: "Dark Web Data Leaks Monitoring",
    description:
      "Continuous monitoring of dark web marketplaces, paste sites, and breach forums for mentions of your organization, brands, and digital assets. Alerts surface in near real time, giving security teams the lead time needed to act before leaked data is weaponized.",
  },
];
